// Selector pre-L2 (F0-5).
//
// Estados:
//   - Loading → spinner
//   - Sin programaciones HOY → estado vacío con CTA "Contactar coordinador"
//   - 1 programación → redirige automáticamente a /capturar/:lineaId
//   - >1 programaciones → cards seleccionables (una por línea)
//   - Fuera de turno → dialog "¿estás capturando fuera de turno?" con razón ≥20 chars
//
// Si el supervisor delega mid-turno, la lista cambia (refetch automático al
// volver a la pantalla; staleTime: 60s).

import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { LiderLayout } from '../../components/layout/LiderLayout';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/spinner';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../lib/auth';
import { useContextoLider } from '../../lib/api/queries';
import { EstadoTurnoCard } from './EstadoTurnoCard';

export function SelectorLineaPage() {
  const { state, signOut } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch, isFetching } = useContextoLider();
  const [outsideTurnoOpen, setOutsideTurnoOpen] = useState(false);
  const [razon, setRazon] = useState('');
  const [pendingLineaId, setPendingLineaId] = useState<string | null>(null);

  // Una línea es_tienda rutea al flujo de captura por corrida (planilla); el
  // resto, al L2 hora-a-hora.
  const rutaCaptura = (lineaId: string, esTienda: boolean) =>
    esTienda ? `/capturar-tienda/${lineaId}` : `/capturar/${lineaId}`;

  // Redirect automático si hay UNA sola línea HOY. Para es_tienda no aplica la
  // puerta de "fuera de turno" del L2 (la ventana la valida el backend de tiendas).
  useEffect(() => {
    if (!data) return;
    const unica = data.programaciones_hoy[0];
    if (data.programaciones_hoy.length !== 1 || !unica) return;
    if (unica.linea.es_tienda) {
      navigate(rutaCaptura(unica.linea.id, true), { replace: true });
    } else if (!data.es_fuera_turno) {
      navigate(rutaCaptura(unica.linea.id, false), { replace: true });
    }
  }, [data, navigate]);

  // Si está fuera de turno y hay programaciones de LÍNEA NORMAL, abrir el modal de
  // razón. Las líneas es_tienda no usan esta puerta (la ventana la valida el backend
  // de tiendas) → no abrir el modal para un líder que hoy solo hace tiendas.
  useEffect(() => {
    const tieneLineaNormal = data?.programaciones_hoy.some((p) => !p.linea.es_tienda) ?? false;
    if (data && data.es_fuera_turno && tieneLineaNormal) {
      setOutsideTurnoOpen(true);
    }
  }, [data]);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando su turno…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar su contexto</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
        <Button onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Reintentando…' : 'Reintentar'}
        </Button>
        <button onClick={() => signOut()} className="text-xs text-fg-tertiary underline">
          Cerrar sesión
        </button>
      </main>
    );
  }

  // Sin programaciones HOY: antes del estado vacío genérico, chequear si el
  // día no opera o el turno arranca dentro de las próximas 12h (design
  // horario-turno-dia §6) — el escenario Robotpack "domingo 23:00" del spec.
  if (data.programaciones_hoy.length === 0) {
    if (data.proximo_inicio || data.dia_sin_turno) {
      return (
        <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 p-6 text-center">
          <EstadoTurnoCard
            proximoInicio={data.proximo_inicio}
            diaSinTurno={data.dia_sin_turno}
            ahoraLocal={`${data.fecha_hoy}T${data.hora_servidor}`}
          />
          <button
            onClick={() => signOut()}
            className="mt-4 text-xs text-fg-tertiary underline hover:text-fg-secondary"
          >
            Cerrar sesión
          </button>
        </main>
      );
    }
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-xl font-medium text-fg">Sin asignación para hoy</h1>
        <p className="text-sm text-fg-secondary">
          Hola <strong>{data.user.nombre}</strong>. No tiene líneas programadas hoy en{' '}
          <strong>{data.tenant.nombre}</strong>.
        </p>
        <p className="text-sm text-fg-tertiary">
          Contacte al coordinador de su planta para revisar su asignación.
        </p>
        <button
          onClick={() => signOut()}
          className="mt-4 text-xs text-fg-tertiary underline hover:text-fg-secondary"
        >
          Cerrar sesión
        </button>
      </main>
    );
  }

  // 1 línea → ya estamos redirigiendo en el useEffect (o esperando confirmación fuera-turno)
  // >1 líneas → mostrar lista
  return (
    <LiderLayout>
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-5">
        <header>
          <p className="text-[10px] uppercase tracking-[0.12em] text-fg-tertiary">
            {data.tenant.nombre}
          </p>
          <h1 className="mt-1 text-xl font-medium text-fg">
            Hola {data.user.nombre} — elija la línea
          </h1>
        </header>

        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {data.programaciones_hoy.map((p) => {
            const esLineaActual = !data.es_fuera_turno && p.turno.id === data.turno_actual_id;
            return (
              <Link
                key={p.programacion_id}
                to={rutaCaptura(p.linea.id, p.linea.es_tienda)}
                className="block"
                onClick={(e) => {
                  // La puerta de "fuera de turno" es solo del L2 hora-a-hora; las
                  // tiendas validan la ventana en el backend.
                  if (data.es_fuera_turno && !p.linea.es_tienda) {
                    e.preventDefault();
                    setPendingLineaId(p.linea.id);
                    setOutsideTurnoOpen(true);
                  }
                }}
              >
                <Card className="transition-colors hover:border-border-stronger">
                  <CardHeader>
                    <CardTitle className="text-base">{p.linea.nombre}</CardTitle>
                    <CardDescription>
                      Turno {p.turno.numero} ({p.turno.hora_inicio.slice(0, 5)}–
                      {p.turno.hora_fin.slice(0, 5)}){esLineaActual ? ' · en curso' : ''}
                      {p.es_linea_principal ? ' · principal' : ''}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-xs text-fg-tertiary">
                    Hora servidor: {data.hora_servidor}
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </section>

        <Dialog open={outsideTurnoOpen} onClose={() => setOutsideTurnoOpen(false)} forceConfirm>
          <DialogHeader>
            <DialogTitle>¿Capturando fuera de turno?</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <p className="mb-3 text-sm text-fg-secondary">
              El servidor dice que son las <strong>{data.hora_servidor}</strong> y ese horario no
              cae dentro del turno asignado. Si va a registrar igualmente, escriba brevemente la
              razón (mínimo 20 caracteres).
            </p>
            <Label htmlFor="razon">Razón</Label>
            <Textarea
              id="razon"
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
              placeholder="Ej: turno extendido por pico de demanda solicitado por supervisor"
              className="mt-1"
            />
            <p className={`mt-1 text-xs ${razon.length >= 20 ? 'text-ok' : 'text-fg-tertiary'}`}>
              {razon.length}/20 caracteres mínimos
            </p>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOutsideTurnoOpen(false);
                setRazon('');
                setPendingLineaId(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              disabled={razon.length < 20}
              onClick={() => {
                const lineaId = pendingLineaId ?? data.programaciones_hoy[0]?.linea.id ?? null;
                if (!lineaId) return;
                const url = `/capturar/${lineaId}?fueraTurno=1&razon=${encodeURIComponent(razon)}`;
                setOutsideTurnoOpen(false);
                navigate(url);
              }}
            >
              Continuar
            </Button>
          </DialogFooter>
        </Dialog>
      </div>
    </LiderLayout>
  );
}
